/**
 * pagePatterns — 면(페이지) 배열 패턴. React·DOM 의존이 없는 순수 모듈.
 *
 * 면은 좌·우 2단이고 단은 문항 1개(한 단) 또는 2개(반 단)를 담는다. 문항은 번호 순서를 지키므로
 * 가능한 배열은 넷으로 닫힌다 — 가운데 문항은 어떤 경우에도 한 단이 될 수 없다.
 *
 *   A: 반·반 | 반·반 (4문항)   B: 한 단 | 반·반 (3문항)
 *   C: 반·반 | 한 단 (3문항)   D: 한 단 | 한 단 (2문항)
 *   E: 한 단 | (빈)   ← 사용자가 고를 수 없다. 강등의 바닥·잔여 면 전용.
 *
 * suneung 프리셋은 수능 수학 3개년(2024·2025·2026 × 홀·짝수형) 실측 배치를 그대로 옮긴 것이다.
 * 근거·측정 방법: docs/print/b4-suneung-page-layout-plan.html
 *
 * ※ 배치 높이 검증에 유형 마커 밴드는 포함하지 않는다 — 현재 인쇄 결과를 바꾸지 않기 위함
 *   (B4는 기본으로 1번에 "5지선다형" 마커가 붙는다). 상세는 작업지시서 §3 주의.
 */

/** 각 패턴의 단 구성. 'hh' = 반 단 2칸, 'f' = 한 단 1칸, null = 빈 단 */
export const PATTERNS = {
  A: { count: 4, left: 'hh', right: 'hh' },
  B: { count: 3, left: 'f', right: 'hh' },
  C: { count: 3, left: 'hh', right: 'f' },
  D: { count: 2, left: 'f', right: 'f' },
  E: { count: 1, left: 'f', right: null },
};

/** 사용자가 고를 수 있는 패턴(E 제외) */
export const SELECTABLE_PATTERNS = ['A', 'B', 'C', 'D'];

export const PATTERN_LABEL = {
  A: 'A · 4문항 (반·반 | 반·반)',
  B: 'B · 3문항 (좌단을 한 문항이 다 씀)',
  C: 'C · 3문항 (우단을 한 문항이 다 씀)',
  D: 'D · 2문항 (양쪽 다 한 단)',
};

/**
 * 프리셋 — head는 면 순서대로, tail은 그 뒤 전부.
 * tail/head 값: 패턴 키 | 'BC'(3문항, 좌·우는 높이로 결정) | 'auto'(그 면은 현행 그리디)
 */
export const PRESETS = {
  // 목록 순서 = 셀렉트 표시 순서. B4 기본값인 수능 표준을 맨 위에 둔다(A4에서는 걸러진다).
  suneung: { label: '수능 표준', head: ['A', 'C', 'C', 'D', 'D', 'B', 'C', 'D'], tail: 'D', papers: ['B4'] },
  auto: { label: '자동 (높이 기준)', head: [], tail: 'auto' },
  even4: { label: '균등 · 면당 4문항', head: [], tail: 'A' },
  even3: { label: '균등 · 면당 3문항', head: [], tail: 'BC' },
  even2: { label: '균등 · 면당 2문항', head: [], tail: 'D' },
};

/** 용지별 기본 프리셋. A4는 현행 배치를 그대로 유지한다(= 기존 코드 경로) */
export const DEFAULT_PRESET = { A4: 'auto', B4: 'suneung' };

/** 그 용지에서 고를 수 있는 프리셋 키 (suneung은 B4 전용) */
export function presetKeysFor(paperKey) {
  return Object.keys(PRESETS).filter((k) => !PRESETS[k].papers || PRESETS[k].papers.includes(paperKey));
}

/** (면 인덱스) → 패턴 키. 면별 지정이 프리셋을 이긴다. */
export function makePatternOf(presetKey, overrides) {
  const preset = PRESETS[presetKey] || PRESETS.auto;
  return (pageIndex) => {
    const picked = overrides && overrides[pageIndex];
    if (picked) return picked;
    return preset.head[pageIndex] != null ? preset.head[pageIndex] : preset.tail;
  };
}

/**
 * 전 면이 자동인가 — true면 호출부가 patternOf를 아예 넘기지 않아
 * 기존 배치 코드 경로가 그대로 실행된다(A4 기본 상태의 회귀 방지 장치).
 */
export function isAllAuto(presetKey, overrides) {
  const preset = PRESETS[presetKey] || PRESETS.auto;
  if (preset.tail !== 'auto' || preset.head.length > 0) return false;
  return !overrides || Object.keys(overrides).length === 0;
}

/** 배치된 면의 모양 → 패턴 키. 자동 면에서 어떤 배열이 나왔는지 표시하는 용도(파생 전용). */
export function patternOfPage(page) {
  if (!page) return null;
  const left = page.left || [];
  const right = page.right || [];
  const lFull = left.length === 1 && !!left[0].useFullColumn;
  const rFull = right.length === 1 && !!right[0].useFullColumn;
  const lHalf = left.length === 2 && !left[0].useFullColumn;
  const rHalf = right.length === 2 && !right[0].useFullColumn;
  if (lHalf && rHalf) return 'A';
  if (lFull && rHalf) return 'B';
  if (lHalf && rFull) return 'C';
  if (lFull && rFull) return 'D';
  if (lFull && right.length === 0) return 'E';
  return null; // 자동 배치가 만든 부분 배치(빈 반 슬롯 등) — 네 패턴에 해당 없음
}

/**
 * 한 면을 패턴으로 배열한다. 넣을 수 없으면 null.
 * queue = 남은 문항의 인덱스 배열(0-based), heights = 문항 인덱스별 측정 높이(mm)
 */
export function arrangePattern(queue, key, heights, slotH, areaH) {
  const def = PATTERNS[key];
  if (!def || queue.length < def.count) return null;
  const leftCount = def.left === 'hh' ? 2 : 1;
  const left = queue.slice(0, leftCount);
  const right = queue.slice(leftCount, def.count);
  const fitsHalf = (idx) => heights[idx] <= slotH;
  const fitsFull = (idx) => heights[idx] <= areaH;

  const leftOk = def.left === 'hh' ? left.every(fitsHalf) : fitsFull(left[0]);
  const rightOk = def.right === null ? true : def.right === 'hh' ? right.every(fitsHalf) : fitsFull(right[0]);
  if (!leftOk || !rightOk) return null;

  const full = [];
  if (def.left === 'f') full.push(left[0]);
  if (def.right === 'f' && right.length > 0) full.push(right[0]);
  return { left, right, full, pattern: key };
}

/**
 * 물러나는 순서. 같은 문항 수의 다른 배열을 먼저 본다.
 *
 * ★ B/C 선택은 폴백 체인이 아니라 "둘 다 평가한 뒤 첫·끝 중 높은 쪽에 한 단"이다.
 *   'C 먼저, 실패하면 B'로 짜면 C도 실현 가능한 면에서 B가 선택되지 않아
 *   수능 6면(15번)이 어긋난다(실측 대조 8/8 → 7/8).
 */
export function ladderFor(want, queue, heights) {
  const head = queue[0];
  const tail = queue.length > 2 ? queue[2] : queue[queue.length - 1];
  const bc = heights[head] >= heights[tail] ? ['B', 'C'] : ['C', 'B'];
  switch (want) {
    case 'A': return ['A'].concat(bc, ['D', 'E']);
    case 'B': return ['B', 'C', 'D', 'E'];
    case 'C': return ['C', 'B', 'D', 'E'];
    case 'D': return ['D', 'E'];
    case 'BC': return bc.concat(['D', 'E']);
    default: return ['E'];
  }
}

/**
 * 자동 면 한 장 채우기 — 현행 distributeProblemsV4의 그리디를 "면 단위"로 옮긴 것.
 *
 * carriedFull — 현행 알고리즘의 미묘한 특성을 재현하기 위한 인자다.
 *   현행은 "이 문항이 한 단짜리인가"를 **그 시점 면의 슬롯 높이**로 판정한 뒤,
 *   자리가 없으면 그 판정을 안은 채 다음 면으로 넘긴다. 첫 면과 이후 면의 슬롯 높이가
 *   다르므로(B4 133.3 / 149.1) 1→2면 경계에서 판정이 갈린다. 이 플래그로 그 결정을 이어받는다.
 *   (무작위 높이 1만 건 등가 검증: scripts/verify-page-patterns.mjs)
 */
export function fillAutoPage(queue, heights, slotH, carriedFull) {
  const left = [];
  const right = [];
  const full = [];
  let i = 0;
  if (i < queue.length && (carriedFull || heights[queue[i]] > slotH)) {
    left.push(queue[i]);
    full.push(queue[i]);
    i += 1;
  } else {
    while (i < queue.length && left.length < 2 && heights[queue[i]] <= slotH) {
      left.push(queue[i]);
      i += 1;
    }
  }
  if (i < queue.length) {
    if (heights[queue[i]] > slotH) {
      right.push(queue[i]);
      full.push(queue[i]);
      i += 1;
    } else {
      while (i < queue.length && right.length < 2 && heights[queue[i]] <= slotH) {
        right.push(queue[i]);
        i += 1;
      }
    }
  }
  return { left, right, full, pattern: 'auto' };
}

/**
 * 패턴 기반 분배. 반환 형태는 기존 distributeProblemsV4와 같고(`{left, right}`)
 * 면 메타 `pat: { want, actual, overflowNo }`가 추가된다 — 렌더러는 이 키를 쓰지 않는다.
 */
export function distributeByPatterns(problems, geo, heights, patternOf) {
  const pages = [];
  let cursor = 0;
  let pageIndex = 0;
  let carriedFull = false; // 직전 자동 면에서 넘겨받은 "한 단" 판정 (fillAutoPage 주석 참고)

  while (cursor < problems.length && pageIndex < 1000) {
    const first = pageIndex === 0;
    const slotH = first ? geo.slotFirstH : geo.slotRestH;
    const areaH = first ? geo.areaFirstH : geo.areaRestH;

    const queue = [];
    for (let k = cursor; k < problems.length && k < cursor + 4; k += 1) queue.push(k);

    const want = patternOf(pageIndex) || 'auto';
    let placed = null;
    let overflowNo = null;

    if (want === 'auto') {
      placed = fillAutoPage(queue, heights, slotH, carriedFull);
      // 현행과 같은 조건에서만 판정을 다음 면으로 넘긴다:
      // 면이 "가득 참"으로 끝난 게 아니라, 두 단이 다 찬 상태에서 긴 문항이 밀려난 경우.
      const nextIdx = cursor + placed.left.length + placed.right.length;
      const leftIsFull = placed.left.length === 1 && placed.full.indexOf(placed.left[0]) >= 0;
      const rightIsFull = placed.right.length === 1 && placed.full.indexOf(placed.right[0]) >= 0;
      const pageIsFull =
        (placed.left.length === 2 && placed.right.length === 2) ||
        (leftIsFull && placed.right.length === 2) ||
        rightIsFull;
      carriedFull =
        nextIdx < problems.length &&
        !pageIsFull &&
        placed.left.length > 0 &&
        placed.right.length > 0 &&
        heights[nextIdx] > slotH;
    } else {
      carriedFull = false;
      const ladder = ladderFor(want, queue, heights);
      for (let k = 0; k < ladder.length && !placed; k += 1) {
        placed = arrangePattern(queue, ladder[k], heights, slotH, areaH);
      }
      if (!placed) {
        // 남은 문항이 목표보다 적거나 모든 배열이 불가 — 남은 수로 만들 수 있는 최대를 쓴다
        const rest = queue.length === 1 ? ['E'] : queue.length === 2 ? ['D', 'E'] : ['C', 'B', 'D', 'E'];
        for (let k = 0; k < rest.length && !placed; k += 1) {
          placed = arrangePattern(queue, rest[k], heights, slotH, areaH);
        }
      }
      if (!placed) {
        // 한 단에도 들어가지 않는 문항 — 단독 배치하고 넘침을 알린다(인쇄를 막지는 않는다)
        placed = { left: [queue[0]], right: [], full: [queue[0]], pattern: 'E' };
        overflowNo = queue[0] + 1;
      }
    }

    const toItems = (indices, columnIsFull) => indices.map((idx, pos) => {
      const item = {
        ...problems[idx],
        startHeight: columnIsFull ? 0 : pos === 0 ? 0 : slotH,
        height: heights[idx],
        problemNumber: idx + 1,
      };
      if (columnIsFull) item.useFullColumn = true;
      return item;
    });

    const leftFull = placed.left.length > 0 && placed.full.indexOf(placed.left[0]) >= 0;
    const rightFull = placed.right.length > 0 && placed.full.indexOf(placed.right[0]) >= 0;

    pages.push({
      left: toItems(placed.left, leftFull),
      right: toItems(placed.right, rightFull),
      pat: { want, actual: placed.pattern, overflowNo },
    });

    cursor += placed.left.length + placed.right.length;
    pageIndex += 1;
  }

  return pages;
}
